import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Autocomplete,
  TextField,
  Avatar,
  Typography,
  Card,
  CardContent,
  Divider,
  Badge,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../../services/api';

export default function EnrollmentsTab({ courseId }) {
  const [enrollments, setEnrollments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialogs
  const [enrollCompanyDialog, setEnrollCompanyDialog] = useState(false);
  const [enrollUserDialog, setEnrollUserDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedUserObj, setSelectedUserObj] = useState(null);

  const loadEnrollments = useCallback(async () => {
    try {
      const res = await api.get(`/enrollments/course/${courseId}`);
      setEnrollments(res.data);
    } catch (err) {
      setError('Erro ao carregar matrículas');
    }
  }, [courseId]);

  useEffect(() => {
    if (courseId) {
      loadEnrollments();
      loadCompanies();
      loadUsers();
    }
  }, [courseId, loadEnrollments]);

  const loadCompanies = async () => {
    try {
      const res = await api.get('/companies');
      setCompanies(res.data);
    } catch (err) {
      console.error('Erro ao carregar empresas', err);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (err) {
      console.error('Erro ao carregar usuários', err);
    }
  };

  const handleEnrollCompany = async () => {
    if (!selectedCompany) return;
    setLoading(true);
    setError(null);
    try {
      await api.post(`/enrollments/course/${courseId}/enroll-company`, {
        companyId: selectedCompany,
      });
      setSuccess('Empresa matriculada com sucesso');
      loadEnrollments();
      setEnrollCompanyDialog(false);
      setSelectedCompany('');
    } catch (err) {
      setError('Erro ao matricular empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollUser = async () => {
    if (!selectedUserObj) return;
    setLoading(true);
    setError(null);
    try {
      await api.post(`/enrollments/course/${courseId}/enroll-user`, { userId: selectedUserObj.id });
      setSuccess('Usuário matriculado com sucesso');
      loadEnrollments();
      setEnrollUserDialog(false);
      setSelectedUserObj(null);
    } catch (err) {
      setError('Erro ao matricular usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleUnenrollUser = async userId => {
    if (!window.confirm('Tem certeza que deseja desmatricular este usuário?')) return;
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/enrollments/course/${courseId}/user/${userId}`);
      setSuccess('Usuário desmatriculado com sucesso');
      loadEnrollments();
    } catch (err) {
      setError('Erro ao desmatricular usuário');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'ENROLLED':
        return 'success';
      case 'COMPLETED':
        return 'primary';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = status => {
    switch (status) {
      case 'ENROLLED':
        return 'Matriculado';
      case 'COMPLETED':
        return 'Concluído';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return status;
    }
  };

  return (
    <Box>
      {/* Header with Stats */}
      <Card
        sx={{
          mb: 3,
          background:
            'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          border: '1px solid rgba(102, 126, 234, 0.2)',
          backgroundColor: 'rgba(30, 41, 59, 0.6)',
        }}
      >
        <CardContent sx={{ backgroundColor: 'transparent' }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <PeopleIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff' }}>
                  Matrículas do Curso
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Total: {enrollments.length} estudantes
                </Typography>
              </Box>
            </Box>
            <Badge badgeContent={enrollments.length} color="primary">
              <SchoolIcon sx={{ color: '#667eea', fontSize: '2rem' }} />
            </Badge>
          </Box>

          <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<BusinessIcon />}
              onClick={() => setEnrollCompanyDialog(true)}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Matricular Empresa
            </Button>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => setEnrollUserDialog(true)}
              sx={{
                borderColor: '#667eea',
                color: '#667eea',
                borderRadius: '12px',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  borderColor: '#5a67d8',
                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Matricular Usuário
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: '12px',
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 2,
            borderRadius: '12px',
            border: '1px solid rgba(34, 197, 94, 0.2)',
          }}
        >
          {success}
        </Alert>
      )}

      {/* Enrollments Table */}
      <Card
        sx={{
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
          backgroundColor: 'rgba(30, 41, 59, 0.6)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background:
                    'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(6, 182, 212, 0.1) 100%)',
                  borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
                }}
              >
                <TableCell sx={{ fontWeight: 700, color: '#ffffff' }}>Estudante</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#ffffff' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#ffffff' }}>Empresa</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#ffffff' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#ffffff' }}>Data de Matrícula</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#ffffff', textAlign: 'center' }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enrollments.map((enrollment, index) => (
                <TableRow
                  key={enrollment.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(59, 130, 246, 0.05)',
                    },
                    backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(59, 130, 246, 0.02)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          fontSize: '0.875rem',
                        }}
                      >
                        {enrollment.student.fullName.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#ffffff' }}>
                        {enrollment.student.fullName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '1rem' }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                        {enrollment.student.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '1rem' }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                        {enrollment.company.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(enrollment.status)}
                      color={getStatusColor(enrollment.status)}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        borderRadius: '8px',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      {new Date(enrollment.enrolledAt).toLocaleDateString('pt-BR')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={() => handleUnenrollUser(enrollment.student.id)}
                      disabled={loading}
                      sx={{
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        color: '#ef4444',
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: 'rgba(239, 68, 68, 0.2)',
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {enrollments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      Nenhuma matrícula encontrada
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog para matricular empresa */}
      <Dialog
        open={enrollCompanyDialog}
        onClose={() => setEnrollCompanyDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '16px 16px 0 0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BusinessIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Matricular Empresa
            </Typography>
          </Box>
          <IconButton onClick={() => setEnrollCompanyDialog(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 3,
            backgroundColor: 'rgba(30, 41, 59, 0.8)',
            borderRadius: '0 0 16px 16px',
          }}
        >
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': { color: '#667eea' },
              }}
            >
              Empresa
            </InputLabel>
            <Select
              value={selectedCompany}
              onChange={e => setSelectedCompany(e.target.value)}
              label="Empresa"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                color: '#ffffff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#667eea',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#667eea',
                },
                '& .MuiSelect-icon': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiMenuItem-root': {
                  color: '#ffffff',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(30, 41, 59, 0.9)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    '& .MuiMenuItem-root': {
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                      },
                    },
                  },
                },
              }}
            >
              {companies.map(company => (
                <MenuItem key={company.id} value={company.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                      {company.name.charAt(0)}
                    </Avatar>
                    <Typography sx={{ color: '#ffffff' }}>{company.name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            backgroundColor: 'rgba(30, 41, 59, 0.9)',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Button
            onClick={() => setEnrollCompanyDialog(false)}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEnrollCompany}
            disabled={loading}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '8px',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
              },
            }}
          >
            Matricular
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para matricular usuário */}
      <Dialog
        open={enrollUserDialog}
        onClose={() => setEnrollUserDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '16px 16px 0 0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PersonAddIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Matricular Usuário
            </Typography>
          </Box>
          <IconButton onClick={() => setEnrollUserDialog(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 3,
            backgroundColor: 'rgba(30, 41, 59, 0.8)',
            borderRadius: '0 0 16px 16px',
          }}
        >
          <Autocomplete
            options={users}
            getOptionLabel={option => `${option.fullName} (${option.email})`}
            value={selectedUserObj}
            onChange={(e, newValue) => setSelectedUserObj(newValue)}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  },
                }}
              >
                <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                  {option.fullName.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#ffffff' }}>
                    {option.fullName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {option.email}
                  </Typography>
                </Box>
              </Box>
            )}
            PaperComponent={({ children }) => (
              <Box
                sx={{
                  backgroundColor: 'rgba(30, 41, 59, 0.9)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
                }}
              >
                {children}
              </Box>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label="Usuário"
                variant="outlined"
                fullWidth
                sx={{
                  mt: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    color: '#ffffff',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                    '& .MuiInputBase-input': {
                      color: '#ffffff',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: 'rgba(255, 255, 255, 0.5)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: '#667eea',
                    },
                  },
                  '& .MuiAutocomplete-popupIndicator': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiAutocomplete-clearIndicator': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            backgroundColor: 'rgba(30, 41, 59, 0.9)',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Button
            onClick={() => setEnrollUserDialog(false)}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEnrollUser}
            disabled={loading}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '8px',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
              },
            }}
          >
            Matricular
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
